import Navbar from "@/components/unsf/Navbar"
import Footer from "@/components/unsf/Footer"
import UnsfHead, { UnsfMeta } from "@/components/unsf/UnsfHead"
import type { CartData } from "@/components/unsf/Cart"

export interface UnsfNavbarProps {
  logo_white: string
  logo_black: string
  labels: { store: string; about: string; cart: string }
  urls: { root: string; store: string; about: string }
  locale: { current: string; other: string; switch_url: string }
}

export interface UnsfChromeCart extends CartData {}

export interface UnsfFooterProps {
  logo_white: string
  labels: {
    brand: string
    about_us: string
    contact: string
    help_legal: string
    returns: string
    privacy: string
    terms: string
    socials: string
  }
  urls: {
    about: string
    contact: string
    returns: string
    confidentiality: string
    cgv: string
    instagram: string
  }
}

interface LegalLayoutProps {
  meta: UnsfMeta
  title: string
  intro: string
  navbar: UnsfNavbarProps
  footer: UnsfFooterProps
  csrfToken: string
  cart: CartData
  children: React.ReactNode
}

export default function LegalLayout({
  meta,
  title,
  intro,
  navbar,
  footer,
  csrfToken,
  cart,
  children,
}: LegalLayoutProps) {
  const breadcrumbs = [
    {
      name: "UNSF Clothing",
      url: meta.canonical_url.replace(/\/[^/]*$/, "/"),
    },
    { name: title, url: meta.canonical_url },
  ]

  return (
    <>
      <UnsfHead meta={meta} breadcrumbs={breadcrumbs} />

      <Navbar
        isHome={false}
        logoWhite={navbar.logo_white}
        logoBlack={navbar.logo_black}
        labels={navbar.labels}
        urls={navbar.urls}
        locale={{
          current: navbar.locale.current,
          other: navbar.locale.other,
          switchUrl: navbar.locale.switch_url,
        }}
        csrfToken={csrfToken}
        cart={cart}
      />

      <div className="min-h-screen bg-light pb-12 pt-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-contrast mb-4 font-unsf-title">
              {title}
            </h1>
            <p className="text-content text-sm">{intro}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {children}
          </div>
        </div>
      </div>

      <Footer
        logoWhite={footer.logo_white}
        labels={footer.labels}
        urls={footer.urls}
      />
    </>
  )
}

export function LegalSection({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={`mb-10 ${className ?? ""}`.trim()}>
      <h2 className="text-xl font-bold text-contrast mb-4 font-unsf-title">
        {title}
      </h2>
      {children}
    </section>
  )
}

export function LegalParagraph({ children }: { children: React.ReactNode }) {
  return <p className="text-content leading-relaxed">{children}</p>
}

export function LegalList({ items }: { items: string[] }) {
  return (
    <ul className="text-content space-y-2 list-disc pl-6">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  )
}

export function LegalLabeledRows({
  rows,
  email,
}: {
  rows: Array<{ label: string; value: string }>
  email?: { label: string; address: string }
}) {
  return (
    <div className="bg-light rounded-xl p-6">
      <div className="text-content space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start gap-3">
            <span className="font-bold w-32">{row.label}</span>
            <span>{row.value}</span>
          </div>
        ))}
        {email && (
          <div className="flex items-start gap-3">
            <span className="font-bold w-32">{email.label}</span>
            <span>
              <a
                href={`mailto:${email.address}`}
                className="underline hover:text-contrast"
              >
                {email.address}
              </a>
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export function LegalAddress({
  lines,
  bold,
}: {
  lines: string[]
  bold?: boolean
}) {
  return (
    <div className="bg-light rounded-xl p-6">
      <div className="text-content">
        {lines.map((line, idx) => (
          <div key={idx} className={bold && idx === 0 ? "font-bold" : ""}>
            {line}
          </div>
        ))}
      </div>
    </div>
  )
}
